// ReportWeekdayRates.jsx

import React, { useEffect, useMemo, useState } from "react";
import { Container, Typography } from "@mui/material";
import HoverPaper from "../HoverPaper";
import ToggleButtonTitled from "../../ToggleButtonTitled"; // Adjust the path if needed
import Colours from "../../../helpers/colours";
import EChartsReact from "echarts-for-react";
import NoDataRow from "../../NoDataRow";
import InfoIcon from "../../InfoIcon";

const weekDays = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
];

const METRICS = {
    "Total Price": "revenueTotal",
    "Booking Total": "bookingsTotal",
};

const METRICS_TITLE = {
    "Total Price": "Revenue",
    "Booking Total": "Bookings",
};

const truncateLegendLabel = (label, max = 24) =>
    label.length > max ? `${label.slice(0, max)}…` : label;

export const REPORT_WEEKDAY_RATES = "REPORT_WEEKDAY_RATES";
const ReportWeekdayRates = ({ data, js, metric, description }) => {
    const [selectedMetric, setSelectedMetric] = useState(
        metric === "Booking Total" ? "Booking Total" : "Total Price",
    );

    useEffect(() => {
        if (metric === "Total Price" || metric === "Booking Total") {
            setSelectedMetric(metric);
        }
    }, [metric]);

    const groupedData = useMemo(() => {
        const initialData = weekDays.reduce((acc, day) => {
            acc[day] = {};
            return acc;
        }, {});

        const ratesSet = new Set();

        Object.values(data || {}).forEach((item) => {
            const keyMap = Object.fromEntries(item.keys || []);

            const day = new Date(
                Date.UTC(
                    Number(keyMap.Year),
                    Number(keyMap.Month) - 1,
                    Number(keyMap.Day),
                ),
            ).toLocaleDateString("en-US", {
                weekday: "long",
                timeZone: "UTC",
            });

            if (!weekDays.includes(day)) {
                return;
            }

            const rateName = keyMap.OfferTitle;
            const value = Number(item.result?.[METRICS[selectedMetric]] || 0);

            // Only include visible/current series in legend
            if (value <= 0 || !rateName) {
                return;
            }

            ratesSet.add(rateName);

            if (!initialData[day][rateName]) {
                initialData[day][rateName] = 0;
            }

            initialData[day][rateName] += value;
        });

        return {
            days: initialData,
            rates: Array.from(ratesSet).sort(),
        };
    }, [data, selectedMetric]);

    const formatValue = (value) =>
        js?.numberWithCommas ? js.numberWithCommas(value) : value;

    // const chartHeight = groupedData.rates.length > 6 ? 380 : 320;
    const chartHeight = 320;
    const option = useMemo(() => {
        const series = groupedData.rates.map((rateName) => ({
            name: rateName,
            type: "bar",
            stack: "total",
            emphasis: {
                focus: "series",
            },
            data: weekDays.map((day) => groupedData.days[day]?.[rateName] || 0),
        }));

        return {
            tooltip: {
                trigger: "axis",
                axisPointer: {
                    type: "shadow",
                },
                formatter: (params) => {
                    const total = params.reduce(
                        (acc, item) => acc + Number(item.value || 0),
                        0,
                    );

                    const lines = params
                        .filter((item) => Number(item.value) > 0)
                        .map(
                            (item) =>
                                `${item.marker} ${item.seriesName}: ${formatValue(item.value)}`,
                        );

                    return [
                        params?.[0]?.axisValue,
                        ...lines,
                        `<b>Total: ${formatValue(total)}</b>`,
                    ].join("<br/>");
                },
            },
            legend: {
                type: "scroll",
                bottom: 8,
                left: 16,
                right: 16,
                pageButtonGap: 8,
                formatter: (name) => truncateLegendLabel(name, 18),
            },
            grid: {
                left: 80,
                right: 30,
                top: 60,
                bottom: 110,
                containLabel: true,
            },
            xAxis: {
                type: "category",
                data: weekDays,
            },
            yAxis: {
                type: "value",
                name: METRICS_TITLE[selectedMetric],
            },
            series,
        };
    }, [groupedData, selectedMetric, js]);

    return (
        <>
            <InfoIcon
                title={`${METRICS_TITLE[selectedMetric]} by Weekday and Rate`}
                description={description}
            />

            <Container
                maxWidth={false}
                component={HoverPaper}
                sx={{ p: 2, backgroundColor: Colours.notificationCard }}
            >
                <ToggleButtonTitled
                    TITLE="Show by:"
                    values={{
                        "Total Price": "Revenue",
                        "Booking Total": "Bookings",
                    }}
                    valueSet={selectedMetric}
                    onChange={(e, value) => {
                        setSelectedMetric(value);
                    }}
                    ITEM_HEIGHT="42px"
                    size="medium"
                />
                {groupedData.rates.length === 0 ? (
                    <Typography
                        sx={{
                            py: 6,
                            textAlign: "center",
                            fontSize: "1rem",
                        }}
                    >
                        <NoDataRow text="No data for these filters" justText />
                    </Typography>
                ) : (
                    <EChartsReact
                        style={{ height: chartHeight, width: "100%" }}
                        option={option}
                    />
                )}
            </Container>
        </>
    );
};

export default ReportWeekdayRates;
